/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  useCarousel,
} from '@/components/ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import {
  Calendar,
  BookOpen,
  Sparkles,
  MapPin,
  Search,
  Share2,
  Link2,
  Copy,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import {
  useCreateShareToken,
  useRevokeShareToken,
} from '@/hooks/useShareToken';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { debugLog, debugError } from '@/lib/debug';
import { useTrip, useUpdateTrip } from '@/hooks/useTrip';
import { DailyItineraryComponent } from '@/components/trip/edit/daily-itinerary';
import { SearchResults } from '@/components/search-results';
import { ItineraryItemDialog } from '@/components/trip/edit/itinerary-item-dialog';
import { PaymentGate } from '@/components/payment/PaymentGate';
import { usePaymentGating } from '@/hooks/usePaymentGating';
import { useAuth } from '@/contexts/AuthContext';
import type {
  ItineraryItem,
  DailyItinerary,
} from '../../../../shared/types/itinerary';
import type { ServiceTierLevel } from '../../../../shared/types/base';
import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Utility function to safely convert Firestore timestamp to Date
function toSafeDate(input: any): Date {
  if (!input) {
    return new Date();
  }

  // Handle Firestore Timestamp objects
  if (input && typeof input.toDate === 'function') {
    try {
      const date = input.toDate();
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date;
      }
    } catch (error) {
      console.warn('Error converting Firestore timestamp:', error);
    }
  }

  // Handle string dates
  if (typeof input === 'string') {
    try {
      const date = new Date(input);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } catch (error) {
      console.warn('Error parsing date string:', input, error);
    }
  }

  // Handle Date objects
  if (input instanceof Date) {
    if (!isNaN(input.getTime())) {
      return input;
    }
  }

  // Handle numeric timestamps
  if (typeof input === 'number') {
    try {
      const date = new Date(input);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } catch (error) {
      console.warn('Error creating date from timestamp:', input, error);
    }
  }

  // Fallback to current date
  console.warn('Invalid date input, using current date:', input);
  return new Date();
}

// Normalize various duration representations to minutes (number)
function normalizeDurationMinutes(raw: any): number {
  try {
    if (raw == null) return 60;

    // Number already
    if (typeof raw === 'number' && isFinite(raw)) {
      return raw;
    }

    // String formats: "90", "1h 30m", "PT1H30M", "60m", "2h"
    if (typeof raw === 'string') {
      const s = raw.trim().toLowerCase();
      // ISO 8601 duration like PT1H30M
      const iso = s.match(/^pt(?:(\d+)h)?(?:(\d+)m)?$/i);
      if (iso) {
        const h = iso[1] ? parseInt(iso[1], 10) : 0;
        const m = iso[2] ? parseInt(iso[2], 10) : 0;
        const total = h * 60 + m;
        return total > 0 ? total : 60;
      }
      // Human friendly like "1h 30m" or "90m"
      const hMatch = s.match(/(\d+)\s*h/);
      const mMatch = s.match(/(\d+)\s*m/);
      if (hMatch || mMatch) {
        const h = hMatch ? parseInt(hMatch[1], 10) : 0;
        const m = mMatch ? parseInt(mMatch[1], 10) : 0;
        const total = h * 60 + m;
        return total > 0 ? total : 60;
      }
      // Plain number string (assume minutes)
      const n = parseFloat(s);
      if (!isNaN(n) && isFinite(n)) return n;
    }

    // Object formats
    if (typeof raw === 'object') {
      // Common shapes: { minutes: 90 }, { value: 90, unit: 'MINUTE' },
      // { duration: { minutes: 90 } }, { hours: 1, minutes: 30 }
      const minutes =
        (typeof raw.minutes === 'number' && isFinite(raw.minutes)
          ? raw.minutes
          : undefined) ??
        (raw.duration && typeof raw.duration.minutes === 'number'
          ? raw.duration.minutes
          : undefined);
      if (typeof minutes === 'number') return minutes;

      // hours/minutes split
      const hours =
        (typeof raw.hours === 'number' && isFinite(raw.hours)
          ? raw.hours
          : undefined) ??
        (raw.duration && typeof raw.duration.hours === 'number'
          ? raw.duration.hours
          : undefined);
      if (hours != null) {
        const minsOnly =
          (hours as number) * 60 +
          (typeof raw.minutes === 'number' ? raw.minutes : 0);
        return minsOnly > 0 ? minsOnly : 60;
      }

      // value with unit
      if (typeof raw.value === 'number' && isFinite(raw.value)) {
        const unit = (raw.unit || raw.units || '').toString().toLowerCase();
        if (unit.startsWith('min')) return raw.value;
        if (unit.startsWith('hour') || unit === 'h') return raw.value * 60;
      }
    }

    // Default
    return 60;
  } catch {
    return 60;
  }
}

// Utility function to map Google Places priceLevel to our cost scale
function mapGooglePriceLevelToCostScale(priceLevel: string): string | null {
  // Handle both current priceLevel values and Google Places API format
  const mapping: Record<string, string> = {
    PRICE_LEVEL_FREE: 'PRICE_LEVEL_FREE',
    PRICE_LEVEL_INEXPENSIVE: 'PRICE_LEVEL_INEXPENSIVE',
    PRICE_LEVEL_MODERATE: 'PRICE_LEVEL_MODERATE',
    PRICE_LEVEL_EXPENSIVE: 'PRICE_LEVEL_EXPENSIVE',
    PRICE_LEVEL_VERY_EXPENSIVE: 'PRICE_LEVEL_VERY_EXPENSIVE',
    // Legacy mappings for backward compatibility
    free: 'PRICE_LEVEL_FREE',
    inexpensive: 'PRICE_LEVEL_INEXPENSIVE',
    moderate: 'PRICE_LEVEL_MODERATE',
    expensive: 'PRICE_LEVEL_EXPENSIVE',
    very_expensive: 'PRICE_LEVEL_VERY_EXPENSIVE',
    $: 'PRICE_LEVEL_INEXPENSIVE',
    $$: 'PRICE_LEVEL_MODERATE',
    $$$: 'PRICE_LEVEL_EXPENSIVE',
    $$$$: 'PRICE_LEVEL_VERY_EXPENSIVE',
  };

  return mapping[priceLevel] || 'PRICE_LEVEL_UNSPECIFIED';
}

// Utility function to map Google Places priceLevel to numeric estimate
function mapGooglePriceLevelToAmount(priceLevel: string): number | null {
  const mapping: Record<string, number> = {
    PRICE_LEVEL_FREE: 0,
    PRICE_LEVEL_INEXPENSIVE: 25,
    PRICE_LEVEL_MODERATE: 50,
    PRICE_LEVEL_EXPENSIVE: 100,
    PRICE_LEVEL_VERY_EXPENSIVE: 200,
  };

  return mapping[priceLevel] || null;
}

// Day selector tabs for the carousel - syncs carousel index with parent state
function DayTabs({ itinerary, onDayChange }: { itinerary: any[]; onDayChange: (index: number) => void }) {
  const { currentIndex, setCurrentIndex } = useCarousel();

  // Sync carousel index changes back to parent
  useEffect(() => {
    onDayChange(currentIndex);
  }, [currentIndex, onDayChange]);

  return (
    <>
      {itinerary.map((day: any, index: number) => {
        const dayDate = day.date instanceof Date && !isNaN(day.date.getTime())
          ? day.date
          : new Date(day.date);
        const dateLabel = !isNaN(dayDate.getTime())
          ? format(dayDate, 'MMM d')
          : `Day ${index + 1}`;
        return (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={cn(
              'px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors',
              currentIndex === index
                ? 'bg-terra text-warm-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            Day {index + 1}
            <span className="hidden sm:inline ml-1 text-xs opacity-75">
              {dateLabel}
            </span>
          </button>
        );
      })}
    </>
  );
}

function TripEditPage() {
  const { tripId } = useParams({ from: '/trip/$tripId/edit' });
  const { data: tripData, isLoading, error } = useTrip(tripId);
  const updateTrip = useUpdateTrip(tripId);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const createShareToken = useCreateShareToken(tripId);
  const revokeShareToken = useRevokeShareToken(tripId);

  // Share popover state
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);

  // Payment gating
  const {
    loading: paymentLoading,
    canPerformAction,
    showPaymentPrompt,
    createPaymentSession,
    checkPaymentStatus,
  } = usePaymentGating(tripId);

  // Check for payment success from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');

    if (paymentSuccess === 'true') {
      // Payment was successful, refresh payment status and show success message
      checkPaymentStatus();
      toast.success('Payment successful! You can now edit your trip.', {
        duration: 5000,
      });

      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [checkPaymentStatus]);

  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [selectedDayForMobileAdd, setSelectedDayForMobileAdd] =
    useState<number>(0);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load user profile to check admin status
  useEffect(() => {
    const loadUserProfile = async () => {
      if (currentUser?.uid) {
        try {
          const { UserProfileService } = await import('@/firebase/userProfile');
          const profile = await UserProfileService.getUserProfile(currentUser.uid);
          setIsAdmin(profile?.isAdmin ?? false);
        } catch (error) {
          console.error('Error loading user profile:', error);
          setIsAdmin(false);
        }
      }
    };
    loadUserProfile();
  }, [currentUser]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [dialogItemType, setDialogItemType] = useState<
    | 'lodging'
    | 'restaurant'
    | 'activity'
    | 'custom'
    | 'travel'
  >('custom');
  const [dialogItem, setDialogItem] = useState<ItineraryItem | undefined>(
    undefined
  );
  const [dialogDayIndex, setDialogDayIndex] = useState<number>(0);

  // Duplicate confirmation dialog state
  const [duplicateItem, setDuplicateItem] = useState<{
    item: any;
    type: string;
    dayIndex: number;
  } | null>(null);

  // Drag and drop state
  const [activeDragItem, setActiveDragItem] = useState<any>(null);
  const [draggedItemType, setDraggedItemType] = useState<string>('');

  // Itinerary state
  const [itinerary, setItinerary] = useState<any[]>([]);

  // Handle legacy trip data that doesn't have the new itinerary structure
  const processedItinerary = useMemo(() => {
    const rawItinerary = tripData?.itinerary || [];
    return rawItinerary.map((day: any) => ({
      ...day,
      date: toSafeDate(day.date),

      items: (day.items || []).map((item: any) => ({
        ...item,
        startTime: toSafeDate(item.startTime),
        endTime: item.endTime ? toSafeDate(item.endTime) : null,
      })),
    }));
  }, [tripData?.itinerary]);

  // Update local itinerary when tripData changes
  useEffect(() => {
    if (tripData?.itinerary) {
      setItinerary(processedItinerary);
    }
  }, [tripData?.itinerary, processedItinerary]);

  // Configure drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required to start drag
      },
    })
  );

  // Custom collision detection: prioritize day containers for external drags
  const collisionDetection = (args: any) => {
    // For external drags (from search results), prioritize day containers
    if (draggedItemType && draggedItemType !== 'itinerary-item') {
      const dayContainerCollisions = pointerWithin(args).filter(
        (collision: any) => collision.id.toString().startsWith('day-')
      );
      if (dayContainerCollisions.length > 0) {
        return dayContainerCollisions;
      }
    }
    // For internal drags (reordering), use default closest center
    return closestCenter(args);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading trip...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-destructive">Error loading trip</p>
              <p className="text-sm text-muted-foreground mt-2">
                {error.message}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tripData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-muted-foreground">Trip not found</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const searchResults = tripData.searchResults || {
    activities: [],
    restaurants: [],
  };

  // Get service tier from trip preferences
  const serviceTier: ServiceTierLevel =
    tripData.preferences?.serviceTier || 'self_service';

  // Unified function to safely calculate start time for new items
  const calculateSafeStartTime = (
    dayIndex: number,
    existingItems: ItineraryItem[]
  ): Date => {
    try {
      if (existingItems.length === 0) {
        // First item of the day - start at 8 AM
        const dayDate = toSafeDate(itinerary[dayIndex].date);
        if (isNaN(dayDate.getTime())) {
          throw new Error('Invalid day date');
        }
        const startTime = new Date(dayDate.getTime() + 8 * 60 * 60 * 1000);
        debugLog('log', 'First item of day, starting at 8 AM');
        return startTime;
      } else {
        // Add after the last item with 30 minute buffer
        const lastItem = existingItems[existingItems.length - 1];
        const lastItemStartTime = toSafeDate(lastItem.startTime);

        // Validate the last item start time
        if (isNaN(lastItemStartTime.getTime())) {
          debugLog('log', 'Invalid last item start time, using fallback');
          // If last item has invalid time, use fallback
          const dayDate = toSafeDate(itinerary[dayIndex].date);
          if (isNaN(dayDate.getTime())) {
            throw new Error('Invalid day date');
          }
          const fallbackTime = new Date(dayDate.getTime() + 8 * 60 * 60 * 1000);
          return fallbackTime;
        }

        const lastItemDuration = normalizeDurationMinutes(lastItem.duration);

        // Create a new Date object to avoid any reference issues
        const lastItemEndTime = new Date(lastItemStartTime);
        lastItemEndTime.setMinutes(
          lastItemEndTime.getMinutes() + lastItemDuration
        );

        // Create the new start time with 30 minute buffer
        const startTime = new Date(lastItemEndTime);
        startTime.setMinutes(startTime.getMinutes() + 30);

        // Debug: Log the exact values being validated
        debugLog('log', 'Validation check:', {
          lastItemStartTimeRaw: lastItem.startTime,
          lastItemStartTimeProcessed: lastItemStartTime,
          lastItemStartTimeValid: !isNaN(lastItemStartTime.getTime()),
          lastItemDuration,
          lastItemEndTimeRaw: lastItemEndTime,
          lastItemEndTimeValid: !isNaN(lastItemEndTime.getTime()),
          lastItemEndTimeTimestamp: lastItemEndTime.getTime(),
          startTimeRaw: startTime,
          startTimeValid: !isNaN(startTime.getTime()),
        });

        // Validate all calculated times
        if (isNaN(lastItemEndTime.getTime())) {
          throw new Error('Invalid last item end time calculated');
        }

        if (isNaN(startTime.getTime())) {
          throw new Error('Invalid new start time calculated');
        }

        debugLog('log', 'Adding after last item:', {
          lastItemTitle: lastItem.title,
          lastItemStartTime: lastItemStartTime.toLocaleTimeString(),
          lastItemDuration,
          lastItemEndTime: lastItemEndTime.toLocaleTimeString(),
          newStartTime: startTime.toLocaleTimeString(),
        });
        return startTime;
      }
    } catch (error) {
      console.error('Error calculating start time:', error);
      // Fallback: Use the day's date at 8 AM, NOT current date (fixes issue #39)
      try {
        const dayDate = new Date(itinerary[dayIndex].date);
        if (!isNaN(dayDate.getTime())) {
          dayDate.setHours(8, 0, 0, 0);
          debugLog('log', 'Using day date fallback start time:', dayDate);
          return dayDate;
        }
      } catch (fallbackError) {
        console.error('Could not get day date for fallback:', fallbackError);
      }
      // Absolute last resort: use current date (should rarely happen)
      console.warn('Using current date as absolute last resort - this may indicate a data issue');
      const now = new Date();
      now.setHours(8, 0, 0, 0);
      debugLog('log', 'Using current date fallback start time:', now);
      return now;
    }
  };

  // Handler implementations
  const handleAddFromSearchResults = async (
    item: any,
    type: string
  ): Promise<boolean> => {
    // Check payment status before allowing edits
    if (!canPerformAction()) {
      showPaymentPrompt('add items to your itinerary');
      return false;
    }

    try {
      // Add item to the currently visible day in the carousel
      addItemToDay(item, type, currentDayIndex);

      // Return true to indicate the operation was initiated
      // The actual success/failure will be handled by the mutation callbacks
      return true;
    } catch (error) {
      console.error('Error adding item from search results:', error);
      return false;
    }
  };

  const addItemToDay = (
    draggedItem: any,
    type: string,
    dayIndex: number,
    bypassDuplicateCheck: boolean = false
  ) => {
    if (!itinerary[dayIndex]) {
      debugError('Invalid day index:', dayIndex);
      return;
    }

    // Check for duplicate items on the same day (unless bypassing)
    if (!bypassDuplicateCheck) {
      const itemTitle =
        draggedItem.name ||
        draggedItem.title ||
        draggedItem.displayName ||
        'Untitled';
      const existingItem = itinerary[dayIndex].items.find(
        (item: ItineraryItem) =>
          item.title.toLowerCase() === itemTitle.toLowerCase()
      );

      if (existingItem) {
        // Show duplicate confirmation dialog
        setDuplicateItem({ item: draggedItem, type, dayIndex });
        return;
      }
    }

    try {
      // Get existing items and sort them
      const existingItems = itinerary[dayIndex].items.sort(
        (a: ItineraryItem, b: ItineraryItem) => a.order - b.order
      );

      // Calculate start time using unified function
      const startTime = calculateSafeStartTime(dayIndex, existingItems);

      // For self_service tier, flights and hotels are booked externally by default
      const isExternalBookingDefault =
        serviceTier === 'self_service' &&
        (type === 'lodging' || type === 'travel');

      // Validate start time before creating item
      if (isNaN(startTime.getTime())) {
        throw new Error('Invalid start time calculated');
      }

      // Create base item properties with enhanced mapping
      const duration = normalizeDurationMinutes(
        draggedItem.duration ?? draggedItem.estimatedDuration ?? 60
      );
      const endTime = new Date(startTime.getTime() + duration * 60 * 1000);

      const baseItemProps = {
        id: `${type}-${Date.now()}`,
        title:
          draggedItem.name ||
          draggedItem.title ||
          draggedItem.displayName ||
          'Untitled',
        description:
          draggedItem.generativeSummary ||
          draggedItem.description ||
          draggedItem.editorial_summary ||
          draggedItem.summary ||
          '',
        startTime: startTime,
        endTime: endTime,
        duration: duration,
        location: {
          address:
            draggedItem.address ||
            draggedItem.vicinity ||
            draggedItem.formatted_address ||
            '',
          city: tripData.preferences?.destination?.city || '',
          country: tripData.preferences?.destination?.country || '',
          region: tripData.preferences?.destination?.region || '',
          coordinates: draggedItem.location
            ? {
                lat:
                  draggedItem.location.lat ||
                  draggedItem.location.latitude ||
                  0,
                lng:
                  draggedItem.location.lng ||
                  draggedItem.location.longitude ||
                  0,
              }
            : draggedItem.geometry
              ? {
                  lat:
                    draggedItem.geometry.location?.lat ||
                    draggedItem.geometry.lat ||
                    0,
                  lng:
                    draggedItem.geometry.location?.lng ||
                    draggedItem.geometry.lng ||
                    0,
                }
              : draggedItem.lat && draggedItem.lng
                ? {
                    lat: draggedItem.lat,
                    lng: draggedItem.lng,
                  }
                : null,
        },
        isPlaceholder: false,
        status: 'planned' as const,
        order: existingItems.length,
        // Enhanced notes and tips system
        notes: isExternalBookingDefault
          ? 'placeholder'
          : draggedItem.reviewSummary || draggedItem.notes || '',
        tip: draggedItem.editorial_summary || draggedItem.tip || '',
        privateNotes: '',
        priority: 'medium' as const,
        // Enhanced booking and confirmation details
        bookingReference: draggedItem.reference || '',
        bookingUrl:
          draggedItem.url ||
          draggedItem.website ||
          draggedItem.bookingUrl ||
          '',
        confirmationNumber: '',
        externalBooking: isExternalBookingDefault,
        cost:
          draggedItem.price ||
          draggedItem.cost ||
          (draggedItem.priceLevel
            ? {
                amount:
                  mapGooglePriceLevelToAmount(draggedItem.priceLevel) || 50,
                currency: 'USD',
                breakdown: null,
              }
            : null) ||
          null,
        costScale: draggedItem.priceLevel
          ? mapGooglePriceLevelToCostScale(draggedItem.priceLevel) || null
          : draggedItem.costScale || null,
        // Contact and support information
        contactInfo: {
          phone: draggedItem.formatted_phone_number || draggedItem.phone || '',
          email: draggedItem.email || '',
          website: draggedItem.website || draggedItem.url || '',
        },
        // Accessibility and special requirements
        accessibilityNotes: draggedItem.wheelchair_accessible
          ? 'Wheelchair accessible'
          : '',
        specialRequirements: [],
        // Timing and logistics
        arrivalBuffer: 0,
        departureBuffer: 0,
        travelTimeToNext: 0,
        // Weather and seasonal considerations
        weatherDependent:
          type === 'activity' ? draggedItem.outdoor || false : false,
        seasonalAvailability: [],
        // Created/modified tracking
        createdAt: (() => {
          const date = new Date();
          if (isNaN(date.getTime())) {
            throw new Error('Invalid current date');
          }
          return date;
        })(),
        updatedAt: (() => {
          const date = new Date();
          if (isNaN(date.getTime())) {
            throw new Error('Invalid current date');
          }
          return date;
        })(),
        createdBy: 'user' as const,

        // AI suggestion indicator
        suggestedByAI: draggedItem.suggestedByAI || false,
      };

      // Convert search result to itinerary item with proper type-specific properties
      let newItem: ItineraryItem;

      if (type === 'activity') {
        // Clean the activity object to remove undefined fields for Firestore
        const cleanActivity: any = {
          name: draggedItem.name || 'Unknown Activity',
          placeId: draggedItem.placeId || '',
        };

        // Only add fields that have values
        if (draggedItem.address) cleanActivity.address = draggedItem.address;
        if (draggedItem.location && draggedItem.location.lat !== undefined && draggedItem.location.lng !== undefined) {
          cleanActivity.location = draggedItem.location;
        }
        if (draggedItem.rating !== null && draggedItem.rating !== undefined) {
          cleanActivity.rating = draggedItem.rating;
        }
        if (draggedItem.priceLevel) cleanActivity.priceLevel = draggedItem.priceLevel;
        if (draggedItem.types && draggedItem.types.length > 0) {
          cleanActivity.types = draggedItem.types;
        }
        if (draggedItem.websiteUri) cleanActivity.websiteUri = draggedItem.websiteUri;
        if (draggedItem.photos && draggedItem.photos.length > 0) {
          cleanActivity.photos = draggedItem.photos;
        }
        if (draggedItem.reviews && draggedItem.reviews.length > 0) {
          cleanActivity.reviews = draggedItem.reviews;
        }
        if (draggedItem.editorialSummary) {
          cleanActivity.editorialSummary = draggedItem.editorialSummary;
        }
        if (draggedItem.generativeSummary) {
          cleanActivity.generativeSummary = draggedItem.generativeSummary;
        }

        newItem = {
          ...baseItemProps,
          type: 'activity',
          color: '#EF4444',
          suggestedByAI: draggedItem.suggestedByAI || false,
          activity: cleanActivity,
          // ... (copy all the activity-specific properties from handleDragEnd)
          ticketQuantity: draggedItem.ticketQuantity || null,
          category:
            draggedItem.types?.[0] || draggedItem.category || 'sightseeing',
          difficultyLevel: draggedItem.difficulty || null,
          minAge: draggedItem.minAge || null,
          maxGroupSize:
            draggedItem.maxCapacity || draggedItem.maxGroupSize || null,
          requiresBooking:
            draggedItem.requiresReservation ||
            draggedItem.requiresBooking ||
            false,
          estimatedDuration:
            draggedItem.duration || draggedItem.estimatedDuration || null,
          operatingHours: draggedItem.opening_hours
            ? {
                openTime:
                  draggedItem.opening_hours.periods?.[0]?.open?.time || '09:00',
                closeTime:
                  draggedItem.opening_hours.periods?.[0]?.close?.time ||
                  '17:00',
                days: draggedItem.opening_hours.weekday_text || [
                  'Monday',
                  'Tuesday',
                  'Wednesday',
                  'Thursday',
                  'Friday',
                  'Saturday',
                  'Sunday',
                ],
              }
            : null,
          ticketInfo:
            draggedItem.ticketInfo ||
            (draggedItem.price
              ? {
                  price: draggedItem.price,
                  bookingUrl: draggedItem.bookingUrl || draggedItem.website || '',
                  advanceBookingRequired:
                    draggedItem.requiresReservation || false,
                }
              : null),
          whatToExpect: draggedItem.overview || draggedItem.description || '',
          whatToBring: draggedItem.requirements || [],
          fitnessLevel: draggedItem.fitnessLevel || null,
          groupSizeRecommendation: draggedItem.recommendedGroupSize || '',
          bestTimeToVisit: draggedItem.bestTimeToVisit || null,
          photoOpportunities:
            draggedItem.photos?.map((p: any) => p.photo_reference || p.url) ||
            [],
          insiderSecrets: draggedItem.tips || draggedItem.insiderTips || '',
          safetyConsiderations:
            draggedItem.safety || draggedItem.warnings || '',
          alternativeOptions: [],
          languagesAvailable: draggedItem.languages || [],
          includesGuide: draggedItem.guidedTour || false,
          transportationProvided: draggedItem.transportation || false,
          meetingPoint:
            draggedItem.meetingPoint || draggedItem.location || null,
        };
      } else if (type === 'restaurant') {
        newItem = {
          ...baseItemProps,
          type: 'restaurant',
          color: '#F59E0B',
          restaurant: draggedItem,
          // ... (copy all the restaurant-specific properties from handleDragEnd)
          reservationTime: null,
          partySize: null,
          specialRequests: '',
          cuisineType:
            draggedItem.cuisine ||
            draggedItem.types?.find((t: string) =>
              [
                'restaurant',
                'food',
                'meal_takeaway',
                'bakery',
                'cafe',
              ].includes(t)
            ) ||
            '',
          mealType: draggedItem.mealType || null,
          priceRange: draggedItem.price_level || draggedItem.priceRange || null,
          dressCode: draggedItem.dressCode || '',
          reservable: draggedItem.reservable || false,
          reservationConfirmed: false,
          dietaryAccommodations: draggedItem.dietaryOptions || [],
          tablePreferences: null,
          menuRecommendations:
            draggedItem.popularDishes || draggedItem.specialties || [],
          localTips: draggedItem.tips || draggedItem.localSecrets || '',
          averageWaitTime: draggedItem.averageWaitTime || 0,
          peakHours: draggedItem.busyTimes || [],
          paymentMethods: draggedItem.paymentOptions || [],
          serviceCharge: draggedItem.serviceCharge || null,
          kidsFriendly:
            draggedItem.familyFriendly || draggedItem.goodForChildren || false,
          noiseLevel: draggedItem.atmosphere?.noiseLevel || null,
        };
      } else {
        // For other types (hotel, flight, custom), create a basic item
        newItem = {
          ...baseItemProps,
          type: type as any,
          color: '#6B7280',
          customData: draggedItem,
          category: draggedItem.category || draggedItem.types?.[0] || 'general',
        };
      }

      // Function to recursively remove undefined values from objects
      const removeUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) {
          return null;
        }
        if (Array.isArray(obj)) {
          return obj.map(removeUndefined).filter(item => item !== undefined);
        }
        if (typeof obj === 'object' && obj !== null) {
          const cleaned: any = {};
          Object.keys(obj).forEach(key => {
            const value = removeUndefined(obj[key]);
            if (value !== undefined) {
              cleaned[key] = value;
            }
          });
          return cleaned;
        }
        return obj;
      };

      // Clean the newItem to remove all undefined values
      const cleanedNewItem = removeUndefined(newItem);

      // Update the itinerary
      const newItinerary = [...itinerary];
      const currentItems = [...newItinerary[dayIndex].items];

      // Add to the end
      currentItems.push(cleanedNewItem);

      // Update order values for all items
      const updatedItems = currentItems.map(
        (item: ItineraryItem, index: number) => ({
          ...item,
          order: index,
        })
      );

      newItinerary[dayIndex] = {
        ...newItinerary[dayIndex],
        items: updatedItems,
      };

      // Update the trip with proper error handling
      updateTrip.mutate(
        { itinerary: newItinerary },
        {
          onSuccess: () => {
            // Show success notification only after successful update
            const itemName = cleanedNewItem.title || `${type} item`;
            toast.success(`${itemName} added to ${itinerary[dayIndex].title}!`);
          },
          onError: error => {
            console.error('Failed to update trip:', error);
            toast.error(`Failed to save ${type} item. Please try again.`);
          },
        }
      );

      debugLog('log', 'Item added to day from search results:', {
        type,
        itemTitle: cleanedNewItem.title,
        dayIndex,
        totalItems: updatedItems.length,
      });
    } catch (error) {
      debugError('Error adding item from search results:', error);
      toast.error(`Failed to add ${type} item. Please try again.`);
    }
  };

  const handleAddItem = (dayIndex: number) => {
    return (
      itemType:
        | 'lodging'
        | 'restaurant'
        | 'activity'
        | 'custom'
        | 'travel'
    ) => {
      setDialogMode('add');
      setDialogItemType(itemType);
      setDialogItem(undefined);
      setDialogDayIndex(dayIndex);
      setDialogOpen(true);
    };
  };

  const handleEditItem = (dayIndex: number, item: ItineraryItem) => {
    // Check payment status before allowing edits
    if (!canPerformAction()) {
      showPaymentPrompt('edit itinerary items');
      return;
    }

    setDialogMode('edit');
    setDialogItemType(item.type);
    setDialogItem(item);
    setDialogDayIndex(dayIndex);
    setDialogOpen(true);
  };

  const handleDuplicateConfirm = () => {
    if (duplicateItem) {
      // Close the dialog
      setDuplicateItem(null);

      // Proceed with adding the item (bypassing the duplicate check)
      addItemToDay(
        duplicateItem.item,
        duplicateItem.type,
        duplicateItem.dayIndex,
        true
      );
    }
  };

  const handleDuplicateCancel = () => {
    setDuplicateItem(null);
  };

  const handleDialogSave = (itemData: Partial<ItineraryItem>) => {
    const newItinerary = [...itinerary];

    if (dialogMode === 'add') {
      // Add new item
      const newItem: ItineraryItem = {
        ...itemData,
        id: itemData.id || `${dialogItemType}-${Date.now()}`,
        type: dialogItemType,
        order: newItinerary[dialogDayIndex].items.length,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as ItineraryItem;

      newItinerary[dialogDayIndex] = {
        ...newItinerary[dialogDayIndex],
        items: [...newItinerary[dialogDayIndex].items, newItem],
      };
    } else if (dialogMode === 'edit' && dialogItem) {
      // Edit existing item
      const itemIndex = newItinerary[dialogDayIndex].items.findIndex(
        (item: ItineraryItem) => item.id === dialogItem.id
      );

      if (itemIndex !== -1) {
        const updatedItem = {
          ...dialogItem,
          ...itemData,
          updatedAt: new Date(),
        };

        newItinerary[dialogDayIndex] = {
          ...newItinerary[dialogDayIndex],
          items: [
            ...newItinerary[dialogDayIndex].items.slice(0, itemIndex),
            updatedItem,
            ...newItinerary[dialogDayIndex].items.slice(itemIndex + 1),
          ],
        };
      }
    }

    updateTrip.mutate({ itinerary: newItinerary });
  };

  const handleDeleteItem = (dayIndex: number, itemId: string) => {
    // Check payment status before allowing edits
    if (!canPerformAction()) {
      showPaymentPrompt('remove items from your itinerary');
      return;
    }

    const newItinerary = [...itinerary];
    newItinerary[dayIndex] = {
      ...newItinerary[dayIndex],
      items: newItinerary[dayIndex].items.filter(
        (item: ItineraryItem) => item.id !== itemId
      ),
    };

    updateTrip.mutate({ itinerary: newItinerary });
  };

  const handleUpdateItemTime = (
    itemId: string,
    startTime: Date,
    duration: number
  ) => {
    // Check payment status before allowing edits
    if (!canPerformAction()) {
      showPaymentPrompt('edit item timing');
      return;
    }

    const newItinerary = [...itinerary];

    // Find the item and update it
    for (let dayIndex = 0; dayIndex < newItinerary.length; dayIndex++) {
      const itemIndex = newItinerary[dayIndex].items.findIndex(
        (item: ItineraryItem) => item.id === itemId
      );
      if (itemIndex !== -1) {
        const updatedItem = {
          ...newItinerary[dayIndex].items[itemIndex],
          startTime,
          duration,
          endTime: new Date(startTime.getTime() + duration * 60 * 1000),
        };

        newItinerary[dayIndex] = {
          ...newItinerary[dayIndex],
          items: [
            ...newItinerary[dayIndex].items.slice(0, itemIndex),
            updatedItem,
            ...newItinerary[dayIndex].items.slice(itemIndex + 1),
          ],
        };

        updateTrip.mutate({ itinerary: newItinerary });
        break;
      }
    }
  };

  const handleUpdateDay = (
    dayIndex: number,
    updates: Partial<DailyItinerary>
  ) => {
    // Check payment status before allowing edits
    if (!canPerformAction()) {
      showPaymentPrompt('edit day details');
      return;
    }

    const newItinerary = [...itinerary];
    newItinerary[dayIndex] = {
      ...newItinerary[dayIndex],
      ...updates,
    };

    updateTrip.mutate(
      { itinerary: newItinerary },
      {
        onSuccess: () => {
          toast.success('Day details updated successfully');
        },
        onError: () => {
          toast.error('Failed to update day details');
        },
      }
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeId = active.id as string;

    // Handle dragging itinerary items for reordering
    if (activeId.startsWith('item-')) {
      const itemId = activeId.replace('item-', '');

      // Find the item being dragged
      for (const day of itinerary) {
        const item = day.items.find(
          (item: ItineraryItem) => item.id === itemId
        );
        if (item) {
          setActiveDragItem(item);
          setDraggedItemType('itinerary-item');
          break;
        }
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    // Clear drag state
    setActiveDragItem(null);
    setDraggedItemType('');

    const { active, over } = event;

    if (!over) return;

    // Check payment status before allowing any drag operations
    if (!canPerformAction()) {
      showPaymentPrompt('rearrange your itinerary');
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    debugLog('log', '🐛 Drag Debug:', {
      activeId,
      overId,
      activeData: active.data?.current,
    });

    // Handle drag from search results to itinerary
    // Search results use IDs like "activity-placeId" or "restaurant-placeId"
    if (
      (activeId.startsWith('activity-') ||
        activeId.startsWith('restaurant-')) &&
      overId.startsWith('day-')
    ) {
      const dayIndex = parseInt(overId.replace('day-', ''));
      const type = activeId.startsWith('activity-')
        ? 'activity'
        : 'restaurant';
      const placeId = activeId.replace(`${type}-`, '');

      // Get the dragged item from search results
      let draggedItem: any = null;

      switch (type) {
        case 'activity':
          draggedItem = searchResults.activities?.find(
            item => item.placeId === placeId
          );
          break;
        case 'restaurant':
          draggedItem = searchResults.restaurants?.find(
            item => item.placeId === placeId
          );
          break;
      }

      if (draggedItem && itinerary[dayIndex]) {
        try {
          // SIMPLIFIED: Always add to the end of the list
          const existingItems = itinerary[dayIndex].items.sort(
            (a: ItineraryItem, b: ItineraryItem) => a.order - b.order
          );

          // Calculate start time using unified function
          const startTime = calculateSafeStartTime(dayIndex, existingItems);

          // For self_service tier, flights and hotels are booked externally by default
          const isExternalBookingDefault =
            serviceTier === 'self_service' &&
            (type === 'lodging' || type === 'travel');

          // Create base item properties with enhanced mapping
          const duration = normalizeDurationMinutes(
            draggedItem.duration ?? draggedItem.estimatedDuration ?? 60
          );
          const endTime = new Date(startTime.getTime() + duration * 60 * 1000);

          const baseItemProps = {
            id: `${type}-${Date.now()}`,
            title:
              draggedItem.name ||
              draggedItem.title ||
              draggedItem.displayName ||
              'Untitled',
            description:
              draggedItem.description ||
              draggedItem.editorial_summary ||
              draggedItem.summary ||
              '',
            startTime: startTime,
            endTime: endTime,
            duration: duration,
            location: {
              address:
                draggedItem.address ||
                draggedItem.vicinity ||
                draggedItem.formatted_address ||
                '',
              city: tripData.preferences?.destination?.city || '',
              country: tripData.preferences?.destination?.country || '',
              region: tripData.preferences?.destination?.region || '',
              coordinates: draggedItem.location
                ? {
                    lat:
                      draggedItem.location.lat ||
                      draggedItem.location.latitude ||
                      0,
                    lng:
                      draggedItem.location.lng ||
                      draggedItem.location.longitude ||
                      0,
                  }
                : draggedItem.geometry
                  ? {
                      lat:
                        draggedItem.geometry.location?.lat ||
                        draggedItem.geometry.lat ||
                        0,
                      lng:
                        draggedItem.geometry.location?.lng ||
                        draggedItem.geometry.lng ||
                        0,
                    }
                  : draggedItem.lat && draggedItem.lng
                    ? {
                        lat: draggedItem.lat,
                        lng: draggedItem.lng,
                      }
                    : null,
            },
            isPlaceholder: false,
            status: 'planned' as const,
            order: existingItems.length,
            // Enhanced notes and tips system
            notes: isExternalBookingDefault
              ? 'placeholder'
              : draggedItem.notes || '',
            tip: draggedItem.editorial_summary || draggedItem.tip || '',
            privateNotes: '',
            userInstructions: '',
            priority: 'medium' as const,
            // Enhanced booking and confirmation details
            bookingReference: draggedItem.reference || '',
            bookingUrl:
              draggedItem.url ||
              draggedItem.website ||
              draggedItem.bookingUrl ||
              '',
            confirmationNumber: '',
            externalBooking: isExternalBookingDefault,
            cost:
              draggedItem.price ||
              draggedItem.cost ||
              (draggedItem.priceLevel
                ? {
                    amount:
                      mapGooglePriceLevelToAmount(draggedItem.priceLevel) || 50,
                    currency: 'USD',
                    breakdown: null,
                  }
                : null),
            costScale: draggedItem.priceLevel
              ? mapGooglePriceLevelToCostScale(draggedItem.priceLevel)
              : draggedItem.costScale || null,
            // Contact and support information
            contactInfo: {
              phone:
                draggedItem.formatted_phone_number || draggedItem.phone || '',
              email: draggedItem.email || '',
              website: draggedItem.website || draggedItem.url || '',
            },
            // Accessibility and special requirements
            accessibilityNotes: draggedItem.wheelchair_accessible
              ? 'Wheelchair accessible'
              : '',
            specialRequirements: [],
            // Timing and logistics
            arrivalBuffer: 0,
            departureBuffer: 0,
            travelTimeToNext: 0,
            // Weather and seasonal considerations
            weatherDependent:
              type === 'activity' ? draggedItem.outdoor || false : false,
            seasonalAvailability: [],
            // Created/modified tracking
            createdAt: (() => {
              const date = new Date();
              if (isNaN(date.getTime())) {
                throw new Error('Invalid current date in drag end');
              }
              return date;
            })(),
            updatedAt: (() => {
              const date = new Date();
              if (isNaN(date.getTime())) {
                throw new Error('Invalid current date in drag end');
              }
              return date;
            })(),
            createdBy: 'user' as const,
          };

          // Convert search result to itinerary item with proper type-specific properties
          let newItem: ItineraryItem;

          if (type === 'activity') {
            newItem = {
              ...baseItemProps,
              type: 'activity',
              color: '#EF4444',
              suggestedByAI: draggedItem.suggestedByAI || false,
              activity: draggedItem,
              ticketQuantity: draggedItem.ticketQuantity || null,
              category:
                draggedItem.types?.[0] || draggedItem.category || 'sightseeing',
              difficultyLevel: draggedItem.difficulty || null,
              minAge: draggedItem.minAge || null,
              maxGroupSize:
                draggedItem.maxCapacity || draggedItem.maxGroupSize || null,
              requiresBooking:
                draggedItem.requiresReservation ||
                draggedItem.requiresBooking ||
                false,
              estimatedDuration:
                draggedItem.duration || draggedItem.estimatedDuration || null,
              operatingHours: draggedItem.opening_hours
                ? {
                    openTime:
                      draggedItem.opening_hours.periods?.[0]?.open?.time ||
                      '09:00',
                    closeTime:
                      draggedItem.opening_hours.periods?.[0]?.close?.time ||
                      '17:00',
                    days: draggedItem.opening_hours.weekday_text || [
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                    ],
                  }
                : null,
              ticketInfo:
                draggedItem.ticketInfo ||
                (draggedItem.price
                  ? {
                      price: draggedItem.price,
                      bookingUrl: draggedItem.bookingUrl || draggedItem.website,
                      advanceBookingRequired:
                        draggedItem.requiresReservation || false,
                    }
                  : null),
              whatToExpect:
                draggedItem.overview || draggedItem.description || '',
              whatToBring: draggedItem.requirements || [],
              fitnessLevel: draggedItem.fitnessLevel || null,
              groupSizeRecommendation: draggedItem.recommendedGroupSize || '',
              bestTimeToVisit: draggedItem.bestTimeToVisit || null,
              photoOpportunities:
                draggedItem.photos?.map(
                  (p: any) => p.photo_reference || p.url
                ) || [],
              insiderSecrets: draggedItem.tips || draggedItem.insiderTips || '',
              safetyConsiderations:
                draggedItem.safety || draggedItem.warnings || '',
              alternativeOptions: [],
              languagesAvailable: draggedItem.languages || [],
              includesGuide: draggedItem.guidedTour || false,
              transportationProvided: draggedItem.transportation || false,
              meetingPoint:
                draggedItem.meetingPoint || draggedItem.location || null,
            };
          } else if (type === 'restaurant') {
            newItem = {
              ...baseItemProps,
              type: 'restaurant',
              color: '#F59E0B',
              suggestedByAI: draggedItem.suggestedByAI || false,
              restaurant: draggedItem,
              reservationTime: null,
              partySize: null,
              specialRequests: '',
              cuisineType:
                draggedItem.cuisine ||
                draggedItem.types?.find((t: string) =>
                  [
                    'restaurant',
                    'food',
                    'meal_takeaway',
                    'bakery',
                    'cafe',
                  ].includes(t)
                ) ||
                '',
              mealType: draggedItem.mealType || null,
              priceRange:
                draggedItem.price_level || draggedItem.priceRange || null,
              dressCode: draggedItem.dressCode || '',
              reservable: draggedItem.reservable || false,
              reservationConfirmed: false,
              dietaryAccommodations: draggedItem.dietaryOptions || [],
              tablePreferences: null,
              menuRecommendations:
                draggedItem.popularDishes || draggedItem.specialties || [],
              localTips: draggedItem.tips || draggedItem.localSecrets || '',
              averageWaitTime: draggedItem.averageWaitTime || 0,
              peakHours: draggedItem.busyTimes || [],
              paymentMethods: draggedItem.paymentOptions || [],
              serviceCharge: draggedItem.serviceCharge || null,
              kidsFriendly:
                draggedItem.familyFriendly ||
                draggedItem.goodForChildren ||
                false,
              noiseLevel: draggedItem.atmosphere?.noiseLevel || null,
            };
          } else if (type === 'hotel' || type === 'lodging') {
            newItem = {
              ...baseItemProps,
              type: 'hotel',
              color: '#8B5CF6',
              suggestedByAI: draggedItem.suggestedByAI || false,
              roomNumber: null,
              checkInTime: draggedItem.checkIn || null,
              checkOutTime: draggedItem.checkOut || null,
              hotelName:
                draggedItem.name ||
                draggedItem.hotelName ||
                draggedItem.title ||
                '',
              roomType: draggedItem.roomType || '',
              starRating: draggedItem.starRating || draggedItem.rating || null,
              propertyType: draggedItem.propertyType || null,
              amenities: draggedItem.amenities || [],
            };
          } else if (type === 'flight') {
            // Legacy flight items → create as travel with flight mode
            newItem = {
              ...baseItemProps,
              type: 'travel',
              color: '#10B981',
              travelMode: 'flight',
              carrier: draggedItem.airline || '',
              referenceNumber: draggedItem.flightNumber || '',
              departure: {
                location: draggedItem.departureAirport || '',
                time: null,
              },
              arrival: {
                location: draggedItem.arrivalAirport || '',
                time: null,
              },
            };
          } else {
            // Default to custom type with enhanced data preservation
            newItem = {
              ...baseItemProps,
              type: 'custom',
              color: '#6B7280',
              suggestedByAI: draggedItem.suggestedByAI || false,
              customData: draggedItem,
              category:
                draggedItem.category || draggedItem.types?.[0] || 'general',
            };
          }

          // Update the itinerary
          const newItinerary = [...itinerary];
          const currentItems = [...newItinerary[dayIndex].items];

          // Add to the end (simplified approach)
          currentItems.push(newItem);

          // Update order values for all items
          const updatedItems = currentItems.map(
            (item: ItineraryItem, index: number) => ({
              ...item,
              order: index,
            })
          );

          newItinerary[dayIndex] = {
            ...newItinerary[dayIndex],
            items: updatedItems,
          };

          // Show success notification immediately since we're updating local state
          const itemName = newItem.title || `${type} item`;
          toast.success(
            `${itemName} added to ${itinerary[dayIndex].title || 'Day ' + (dayIndex + 1)}!`
          );

          updateTrip.mutate(
            { itinerary: newItinerary },
            {
              onError: error => {
                console.error('🐛 Trip update failed:', error);
                console.error('Failed to update trip:', error);
                toast.error(`Failed to save ${type} item. Please try again.`);
              },
            }
          );
        } catch (error) {
          debugError('Error creating new item:', error);
          toast.error(`Failed to add ${type} item. Please try again.`);
        }
      }
    }

    // Handle reordering within the same day or moving between days
    if (activeId.startsWith('item-') || !activeId.includes('-')) {
      // This is an existing itinerary item being moved
      const activeItemId = activeId.replace('item-', '');

      // Find the source day and item
      let sourceDayIndex = -1;
      let sourceItem: ItineraryItem | null = null;

      for (let i = 0; i < itinerary.length; i++) {
        const item = itinerary[i].items.find(
          (item: ItineraryItem) => item.id === activeItemId
        );
        if (item) {
          sourceDayIndex = i;
          sourceItem = item;
          break;
        }
      }

      if (sourceItem && sourceDayIndex !== -1) {
        debugLog('log', 'Reordering item:', {
          itemTitle: sourceItem.title,
          sourceDay: sourceDayIndex,
          overId,
        });

        // Determine target day
        let targetDayIndex = -1;
        if (overId.startsWith('day-')) {
          targetDayIndex = parseInt(overId.replace('day-', ''));
        } else {
          // Find the day containing the target item
          const targetItemId = overId.replace('item-', '');
          for (let i = 0; i < itinerary.length; i++) {
            if (
              itinerary[i].items.some(
                (item: ItineraryItem) => item.id === targetItemId
              )
            ) {
              targetDayIndex = i;
              break;
            }
          }
        }

        debugLog('log', 'Target day determined:', targetDayIndex);

        if (targetDayIndex !== -1) {
          const newItinerary = [...itinerary];

          // Handle same-day reordering vs cross-day moves
          if (sourceDayIndex === targetDayIndex) {
            // Same-day reordering: use array manipulation for accuracy
            const dayItems = [...newItinerary[sourceDayIndex].items];

            // Find positions
            const currentIndex = dayItems.findIndex(
              item => item.id === activeItemId
            );
            let targetIndex = dayItems.length - 1;

            if (overId.startsWith('item-')) {
              const targetItemId = overId.replace('item-', '');
              const targetItemIndex = dayItems.findIndex(
                item => item.id === targetItemId
              );

              if (targetItemIndex !== -1) {
                targetIndex = targetItemIndex;
                if (currentIndex < targetItemIndex) {
                  targetIndex = targetItemIndex - 1;
                }
              }
            }

            // Move item to new position
            const [movedItem] = dayItems.splice(currentIndex, 1);
            dayItems.splice(targetIndex, 0, movedItem);

            // Recalculate all items
            const updatedItems: ItineraryItem[] = [];
            dayItems.forEach((item: ItineraryItem, index: number) => {
              let startTime: Date;
              try {
                if (index === 0) {
                  const dayDate = toSafeDate(newItinerary[sourceDayIndex].date);
                  if (isNaN(dayDate.getTime())) {
                    throw new Error('Invalid day date in reordering');
                  }
                  startTime = new Date(dayDate.getTime() + 9 * 60 * 60 * 1000);
                } else {
                  const prevItem = updatedItems[index - 1];
                  const prevStartTime = toSafeDate(prevItem.startTime);
                  if (isNaN(prevStartTime.getTime())) {
                    throw new Error(
                      'Invalid previous item start time in reordering'
                    );
                  }
                  const prevDuration = prevItem.duration || 60;
                  const prevEndTime = new Date(
                    prevStartTime.getTime() + prevDuration * 60 * 1000
                  );
                  startTime = new Date(prevEndTime.getTime() + 30 * 60 * 1000);
                }

                // Validate calculated start time
                if (isNaN(startTime.getTime())) {
                  throw new Error(
                    'Invalid calculated start time in reordering'
                  );
                }

                // Calculate end time if duration exists
                let endTime: Date | null = null;
                if (item.duration) {
                  endTime = new Date(
                    startTime.getTime() + item.duration * 60 * 1000
                  );
                  if (isNaN(endTime.getTime())) {
                    throw new Error(
                      'Invalid calculated end time in reordering'
                    );
                  }
                }

                updatedItems.push({
                  ...item,
                  order: index,
                  startTime,
                  endTime,
                });
              } catch (error) {
                console.error(
                  'Error calculating time for item in reordering:',
                  error,
                  item
                );
                // Use fallback time
                const now = new Date();
                const fallbackStartTime = new Date(
                  now.getFullYear(),
                  now.getMonth(),
                  now.getDate(),
                  9 + index,
                  0,
                  0
                );
                updatedItems.push({
                  ...item,
                  order: index,
                  startTime: fallbackStartTime,
                  endTime: item.duration
                    ? new Date(
                        fallbackStartTime.getTime() + item.duration * 60 * 1000
                      )
                    : null,
                });
              }
            });

            newItinerary[sourceDayIndex] = {
              ...newItinerary[sourceDayIndex],
              items: updatedItems,
            };

            debugLog('log', 'Same-day reorder complete:', {
              itemCount: updatedItems.length,
              itemTitles: updatedItems.map(item => item.title),
            });

            updateTrip.mutate(
              { itinerary: newItinerary },
              {
                onSuccess: () => {
                  debugLog('log', 'Same-day reorder saved successfully');
                },
                onError: error => {
                  debugError('Failed to save same-day reorder:', error);
                },
              }
            );

            return;
          }

          // Cross-day move: remove from source day and recalculate order/times
          const filteredSourceItems = newItinerary[sourceDayIndex].items.filter(
            (item: ItineraryItem) => item.id !== activeItemId
          );

          const sourceItems: ItineraryItem[] = [];
          filteredSourceItems.forEach((item: ItineraryItem, index: number) => {
            // Recalculate start time based on new position
            let startTime: Date;
            try {
              if (index === 0) {
                const dayDate = toSafeDate(newItinerary[sourceDayIndex].date);
                if (isNaN(dayDate.getTime())) {
                  throw new Error('Invalid day date in cross-day move');
                }
                startTime = new Date(dayDate.getTime() + 9 * 60 * 60 * 1000);
              } else {
                const prevItem = sourceItems[index - 1];
                const prevStartTime = toSafeDate(prevItem.startTime);
                if (isNaN(prevStartTime.getTime())) {
                  throw new Error(
                    'Invalid previous item start time in cross-day move'
                  );
                }
                const prevDuration = normalizeDurationMinutes(
                  prevItem.duration
                );
                const prevEndTime = new Date(
                  prevStartTime.getTime() + prevDuration * 60 * 1000
                );
                startTime = new Date(prevEndTime.getTime() + 30 * 60 * 1000);
              }

              // Validate calculated start time
              if (isNaN(startTime.getTime())) {
                throw new Error(
                  'Invalid calculated start time in cross-day move'
                );
              }

              // Calculate end time if duration exists
              let endTime: Date | null = null;
              if (item.duration) {
                endTime = new Date(
                  startTime.getTime() + item.duration * 60 * 1000
                );
                if (isNaN(endTime.getTime())) {
                  throw new Error(
                    'Invalid calculated end time in cross-day move'
                  );
                }
              }

              sourceItems.push({
                ...item,
                order: index,
                startTime,
                endTime,
              });
            } catch (error) {
              console.error(
                'Error calculating time for item in cross-day move:',
                error,
                item
              );
              // Use fallback time
              const now = new Date();
              const fallbackStartTime = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
                9 + index,
                0,
                0
              );
              sourceItems.push({
                ...item,
                order: index,
                startTime: fallbackStartTime,
                endTime: item.duration
                  ? new Date(
                      fallbackStartTime.getTime() + item.duration * 60 * 1000
                    )
                  : null,
              });
            }
          });

          newItinerary[sourceDayIndex] = {
            ...newItinerary[sourceDayIndex],
            items: sourceItems,
          };

          // Calculate insertion position in target day
          let insertIndex = newItinerary[targetDayIndex].items.length; // Default to end

          if (overId.startsWith('item-')) {
            // Dropping on/near a specific item - find its position
            const targetItemId = overId.replace('item-', '');
            const targetItemIndex = newItinerary[
              targetDayIndex
            ].items.findIndex(
              (item: ItineraryItem) => item.id === targetItemId
            );

            if (targetItemIndex !== -1) {
              // Insert after the target item
              insertIndex = targetItemIndex + 1;
            }
          }

          // Insert the item at the calculated position
          const targetDayItems = [...newItinerary[targetDayIndex].items];
          targetDayItems.splice(insertIndex, 0, sourceItem);

          // Recalculate order values and times for all items in the target day
          const updatedItems: ItineraryItem[] = [];
          targetDayItems.forEach((item: ItineraryItem, index: number) => {
            // Calculate start time based on position
            let startTime: Date;
            try {
              if (index === 0) {
                // First item starts at 9 AM
                const dayDate = toSafeDate(newItinerary[targetDayIndex].date);
                if (isNaN(dayDate.getTime())) {
                  throw new Error('Invalid target day date in cross-day move');
                }
                startTime = new Date(dayDate.getTime() + 9 * 60 * 60 * 1000);
              } else {
                // Start 30 minutes after previous item ends
                const prevItem = updatedItems[index - 1];
                const prevStartTime = toSafeDate(prevItem.startTime);
                if (isNaN(prevStartTime.getTime())) {
                  throw new Error(
                    'Invalid previous item start time in target day'
                  );
                }
                const prevDuration = normalizeDurationMinutes(
                  prevItem.duration
                );
                const prevEndTime = new Date(
                  prevStartTime.getTime() + prevDuration * 60 * 1000
                );
                startTime = new Date(prevEndTime.getTime() + 30 * 60 * 1000);
              }

              // Validate calculated start time
              if (isNaN(startTime.getTime())) {
                throw new Error('Invalid calculated start time in target day');
              }

              // Calculate end time if duration exists
              let endTime: Date | null = null;
              if (item.duration) {
                endTime = new Date(
                  startTime.getTime() + item.duration * 60 * 1000
                );
                if (isNaN(endTime.getTime())) {
                  throw new Error('Invalid calculated end time in target day');
                }
              }

              updatedItems.push({
                ...item,
                order: index,
                startTime,
                endTime,
              });
            } catch (error) {
              console.error(
                'Error calculating time for item in target day:',
                error,
                item
              );
              // Use fallback time
              const now = new Date();
              const fallbackStartTime = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
                9 + index,
                0,
                0
              );
              updatedItems.push({
                ...item,
                order: index,
                startTime: fallbackStartTime,
                endTime: item.duration
                  ? new Date(
                      fallbackStartTime.getTime() + item.duration * 60 * 1000
                    )
                  : null,
              });
            }
          });

          newItinerary[targetDayIndex] = {
            ...newItinerary[targetDayIndex],
            items: updatedItems,
          };

          // Debug: Log the changes
          debugLog('log', 'Reordering complete:', {
            sourceDayItems: sourceItems.length,
            targetDayItems: updatedItems.length,
            targetDayItemTitles: updatedItems.map(item => item.title),
          });

          // Update the trip
          updateTrip.mutate(
            { itinerary: newItinerary },
            {
              onSuccess: () => {
                debugLog('log', 'Trip reordering saved successfully');
              },
              onError: error => {
                debugError('Failed to save reordered trip:', error);
              },
            }
          );
        }
      }
    }
  };

  const totalStats = {
    totalDays: itinerary.length,
  };

  return (
    <div className="min-h-screen bg-background">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Header */}
        <div className="border-b bg-card">
          <div className="container mx-auto px-4 py-3 sm:py-4">
            <div className="flex items-start sm:items-center justify-between mb-3 sm:mb-4">
              <div className="min-w-0 flex-1 pr-4">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold truncate">
                  Edit Trip
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground truncate">
                  {tripData.preferences?.destination?.city},{' '}
                  {tripData.preferences?.destination?.country}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs sm:text-sm px-2 sm:px-3"
                  disabled={!canPerformAction()}
                  onClick={() =>
                    navigate({
                      to: '/trip/$tripId/itinerary',
                      params: { tripId },
                    })
                  }
                >
                  <BookOpen className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  View Itinerary
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs sm:text-sm px-2 sm:px-3"
                    >
                      <Share2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Share</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80">
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">Share Trip</h4>
                      {shareToken ? (
                        <>
                          <div className="flex items-center gap-1">
                            <Link2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">
                              Share link:
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Input
                              readOnly
                              value={`${window.location.origin}/trip/${tripId}/itinerary?token=${shareToken}`}
                              className="text-xs h-8"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 flex-shrink-0"
                              onClick={() => {
                                const url = `${window.location.origin}/trip/${tripId}/itinerary?token=${shareToken}`;
                                navigator.clipboard.writeText(url).then(
                                  () => toast.success('Link copied to clipboard!'),
                                  () => toast.info(`Share link: ${url}`)
                                );
                              }}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7 flex-1"
                              disabled={
                                revokeShareToken.isPending ||
                                createShareToken.isPending
                              }
                              onClick={async () => {
                                try {
                                  await revokeShareToken.mutateAsync();
                                  const result =
                                    await createShareToken.mutateAsync();
                                  setShareToken(result.token);
                                  const url = `${window.location.origin}/trip/${tripId}/itinerary?token=${result.token}`;
                                  navigator.clipboard.writeText(url).then(
                                    () =>
                                      toast.success(
                                        'New share link copied to clipboard!'
                                      ),
                                    () => toast.info(`New share link: ${url}`)
                                  );
                                } catch {
                                  // Error toasts handled by mutation hooks
                                }
                              }}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Regenerate
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7 flex-1 text-destructive hover:text-destructive"
                              onClick={() => setRevokeDialogOpen(true)}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Revoke
                            </Button>
                          </div>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          disabled={createShareToken.isPending}
                          onClick={async () => {
                            try {
                              const result =
                                await createShareToken.mutateAsync();
                              setShareToken(result.token);
                              const url = `${window.location.origin}/trip/${tripId}/itinerary?token=${result.token}`;
                              navigator.clipboard.writeText(url).then(
                                () =>
                                  toast.success(
                                    'Share link copied to clipboard!'
                                  ),
                                () => toast.info(`Share link: ${url}`)
                              );
                            } catch {
                              // Error toast handled by mutation hook
                            }
                          }}
                        >
                          <Link2 className="h-3.5 w-3.5 mr-1.5" />
                          Generate Share Link
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Trip Overview Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {/* Duration & Dates Card */}
              <Card className="bg-gradient-to-br from-sage/10 to-sage/5 border-sage/20">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-xs sm:text-sm font-medium text-muted-foreground">
                        Duration
                      </p>
                      <p className="text-lg sm:text-xl lg:text-2xl font-bold text-sage-dark">
                        {totalStats.totalDays}
                      </p>
                      <p className="text-xs text-muted-foreground leading-tight">
                        {tripData.preferences?.dates?.start &&
                        tripData.preferences?.dates?.end ? (
                          <>
                            {new Date(
                              toSafeDate(tripData.preferences.dates.start)
                            ).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}{' '}
                            -{' '}
                            {new Date(
                              toSafeDate(tripData.preferences.dates.end)
                            ).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </>
                        ) : totalStats.totalDays === 1 ? (
                          'Day'
                        ) : (
                          'Days'
                        )}
                      </p>
                    </div>
                    <div className="p-2 bg-sage/15 rounded-full">
                      <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-sage-dark" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Activities Card */}
              <Card className="bg-gradient-to-br from-clay/10 to-clay/5 border-clay/20">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-xs sm:text-sm font-medium text-muted-foreground">
                        Activities
                      </p>
                      <p className="text-lg sm:text-xl lg:text-2xl font-bold text-dusk">
                        {(searchResults.activities?.length || 0) +
                          (searchResults.restaurants?.length || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {searchResults.restaurants?.length || 0} Dining
                      </p>
                    </div>
                    <div className="p-2 bg-clay/15 rounded-full">
                      <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-clay" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Service Tier Card */}
              <Card className="bg-gradient-to-br from-sand/40 to-sand/20 border-sand">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-xs sm:text-sm font-medium text-muted-foreground">
                        Service
                      </p>
                      <p className="text-sm sm:text-base lg:text-lg font-bold text-dusk leading-tight">
                        Self-Service
                      </p>
                      <p className="text-xs text-muted-foreground">Tier</p>
                    </div>
                    <div className="p-2 bg-sand rounded-full">
                      <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-warm-mid" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Itinerary */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Daily Itinerary</h2>
              </div>

              <PaymentGate
                isLocked={!canPerformAction()}
                onUnlockWithType={(paymentType) => {
                  const currentUrl =
                    window.location.pathname + window.location.search;
                  const absoluteUrl = window.location.origin + currentUrl;
                  createPaymentSession(absoluteUrl, undefined, paymentType);
                }}
                loading={paymentLoading}
                title="Unlock Trip Editing"
                description="Customize your itinerary with unlimited edits"
                features={[
                  'Add & remove activities and restaurants',
                  'Reorder your daily schedule',
                  'Edit all trip details and timing',
                  'Export beautiful trip stories',
                  'Full customization control',
                ]}
              >
                <Carousel className="w-full">
                  {/* Day selector tabs */}
                  <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                    <DayTabs itinerary={itinerary} onDayChange={setCurrentDayIndex} />
                    <div className="ml-auto flex items-center gap-1 flex-shrink-0">
                      <CarouselPrevious className="static translate-y-0 h-8 w-8" />
                      <CarouselNext className="static translate-y-0 h-8 w-8" />
                    </div>
                  </div>
                  <CarouselContent className="items-stretch">
                    {itinerary.map((day, index) => (
                      <CarouselItem key={`day-${index}`} className="h-full">
                        <div className="h-[calc(100vh-320px)] min-h-[400px]">
                          <DailyItineraryComponent
                            day={day}
                            dayIndex={index}
                            tripDestination={tripData.preferences?.destination}
                            onAddItem={handleAddItem(index)}
                            onEditItem={item => handleEditItem(index, item)}
                            onDeleteItem={itemId => handleDeleteItem(index, itemId)}
                            onUpdateItemTime={handleUpdateItemTime}
                            onUpdateDay={handleUpdateDay}
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </PaymentGate>

              {itinerary.length === 0 && (
                <Card>
                  <CardContent className="py-8">
                    <div className="text-center">
                      <p className="text-muted-foreground">
                        No itinerary days found.
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Start by browsing options and adding items to create
                        your daily plans.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Search Results (Hidden on mobile) */}
            <div className="hidden lg:block space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">
                  Available Options
                </h2>
                <SearchResults
                  searchResults={searchResults}
                  tripPreferences={tripData.preferences}
                  onAddToItinerary={handleAddFromSearchResults}
                  disabled={!canPerformAction()}
                  isAdmin={isAdmin}
                  isMobile={false}
                  itinerary={itinerary}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Item Dialog */}
        <ItineraryItemDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          mode={dialogMode}
          itemType={dialogItemType}
          item={dialogItem}
          day={
            dialogDayIndex < itinerary.length
              ? itinerary[dialogDayIndex]
              : undefined
          }
          serviceTier={serviceTier}
          tripPreferences={tripData.preferences}
          onSave={handleDialogSave}
        />

        {/* Duplicate Confirmation Dialog */}
        {duplicateItem && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998]"
            style={{ pointerEvents: 'auto' }}
          >
            <div
              className="bg-warm-white rounded-2xl p-6 max-w-md mx-4 shadow-2xl border border-sand"
              style={{ pointerEvents: 'auto' }}
            >
              <h3 className="text-lg font-semibold mb-4">
                Duplicate Item Found
              </h3>
              <p className="text-warm-mid mb-6">
                "
                {duplicateItem.item.name ||
                  duplicateItem.item.title ||
                  'Untitled'}
                " is already on Day {duplicateItem.dayIndex + 1}.
                <br />
                <br />
                Are you sure you want to add it again?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDuplicateCancel}
                  className="flex-1 px-4 py-2 border border-sand rounded-full text-warm-mid hover:bg-sand/50 transition-colors"
                  style={{ pointerEvents: 'auto' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDuplicateConfirm}
                  className="flex-1 px-4 py-2 bg-terra text-warm-white rounded-full hover:bg-rust transition-colors"
                  style={{ pointerEvents: 'auto' }}
                >
                  Add Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drag Overlay */}
        <DragOverlay style={{ zIndex: 9999 }}>
          {activeDragItem ? (
            <div className="bg-warm-white p-3 rounded-2xl shadow-2xl border-2 border-terra opacity-90 transform rotate-2">
              <div className="flex items-center space-x-3">
                {draggedItemType === 'activity' && (
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    A
                  </div>
                )}
                {draggedItemType === 'restaurant' && (
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    R
                  </div>
                )}
                {(draggedItemType === 'hotel' || draggedItemType === 'lodging') && (
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    L
                  </div>
                )}
                {draggedItemType === 'itinerary-item' && (
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    I
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium text-dusk truncate">
                    {activeDragItem.name || activeDragItem.title || 'Untitled'}
                  </p>
                  <p className="text-sm text-warm-mid truncate">
                    {draggedItemType === 'itinerary-item'
                      ? 'Moving item'
                      : 'Adding to itinerary'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </DragOverlay>

        {/* Mobile Search FAB - Only visible on mobile */}
        <Button
          onClick={() => setMobileSearchOpen(true)}
          className="fixed bottom-6 right-6 lg:hidden z-50 h-14 w-14 rounded-full shadow-lg bg-terra hover:bg-rust"
          size="icon"
          disabled={!canPerformAction()}
        >
          <Search className="h-6 w-6" />
        </Button>

        {/* Mobile Search Dialog */}
        <Dialog open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
          <DialogContent
            className="max-w-full h-[90vh] sm:max-w-2xl p-0 overflow-hidden"
            closeButtonClassName="text-terra hover:text-rust opacity-100"
          >
            <DialogHeader className="px-4 pt-4 pb-2 border-b space-y-3">
              <DialogTitle>Browse Options</DialogTitle>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Add to:
                </label>
                <Select
                  value={selectedDayForMobileAdd.toString()}
                  onValueChange={value =>
                    setSelectedDayForMobileAdd(parseInt(value))
                  }
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {itinerary.map((day, index) => (
                      <SelectItem key={index} value={index.toString()}>
                        {day.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </DialogHeader>
            <div className="overflow-y-auto h-full pb-4">
              <SearchResults
                searchResults={searchResults}
                tripPreferences={tripData.preferences}
                onAddToItinerary={async (item, type) => {
                  // Check payment status before allowing edits
                  if (!canPerformAction()) {
                    showPaymentPrompt('add items to your itinerary');
                    return false;
                  }

                  try {
                    // Add item to the selected day
                    addItemToDay(item, type, selectedDayForMobileAdd);
                    // Keep dialog open to allow adding multiple items
                    return true;
                  } catch (error) {
                    console.error(
                      'Error adding item from mobile search:',
                      error
                    );
                    return false;
                  }
                }}
                disabled={!canPerformAction()}
                isAdmin={isAdmin}
                isMobile={true}
                itinerary={itinerary}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Revoke Share Link Confirmation Dialog */}
        <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revoke Share Link</DialogTitle>
              <DialogDescription>
                Anyone with the current link will lose access. This can&apos;t be
                undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRevokeDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={revokeShareToken.isPending}
                onClick={async () => {
                  try {
                    await revokeShareToken.mutateAsync();
                    setShareToken(null);
                    setRevokeDialogOpen(false);
                  } catch {
                    // Error toast handled by mutation hook
                  }
                }}
              >
                Revoke
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DndContext>
    </div>
  );
}

export const Route = createFileRoute('/trip/$tripId/edit')({
  component: TripEditPage,
});
